import { CardActionArea, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';

import { ReactComponent as TimelineFAQSvg } from '../../../assets/timeline_search_faq.svg';
import { ReactComponent as TimelinePostSvg } from '../../../assets/timeline_search_post.svg';
import { AnalyticsItemsFragment } from '../../../graphql/gen/types';
import { bottomBorderStyle, colorPalette, spacings, TextSizesEnum } from '../../../theme';
import { LinesEllipsis } from '../../shared/primitives/LinesEllipsis';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { plainTextPreview } from '../common/plainTextPreview';
import { useDialogState } from '../dialog/useDialogState';
import { logAnalytics } from '../shared/logAnalytics';

interface ItemData {
  icon: React.ReactNode;
  title: string;
  body: string;
  viewOnClick: () => void;
}

interface Props {
  item: AnalyticsItemsFragment['posts']['items'][0] | AnalyticsItemsFragment['questions']['items'][0];
}

export const AnalyticsItem = React.memo(({ item }: Props): React.ReactElement | null => {
  const classes = useStyles();
  const [, setDialogState] = useDialogState();

  const getItemData = (): ItemData | null => {
    switch (item.__typename) {
      case 'Post': {
        return {
          icon: <TimelinePostSvg />,
          title: item.title,
          body: !!item.body ? plainTextPreview(item.body, item.mentions, item.links) : '',
          viewOnClick: (): void => {
            logAnalytics({ name: 'analytics item clicked' });
            setDialogState({ type: 'post_analytics_from_plex', postId: item.id });
          },
        };
      }
      case 'Question': {
        return {
          icon: <TimelineFAQSvg />,
          title: item.text || '',
          body: !!item.acceptedAnswer?.body
            ? plainTextPreview(item.acceptedAnswer.body, item.acceptedAnswer.mentions, item.acceptedAnswer.links)
            : '',
          viewOnClick: (): void => {
            logAnalytics({ name: 'analytics item clicked' });
            setDialogState({ type: 'fact_analytics_from_plex', factId: item.id });
          },
        };
      }
      default: {
        return null;
      }
    }
  };

  const itemData = getItemData();
  if (!itemData) {
    return null;
  }

  return (
    <CardActionArea className={classes.eventRoot} onClick={itemData.viewOnClick}>
      <div className={classNames(classes.columnType)}>{itemData.icon}</div>
      <div className={classNames(classes.columnDescription)}>
        <PlexTypography className={classes.eventTitle} text={itemData.title} size={TextSizesEnum.Normal} />
        <PlexTypography className={classes.eventText} size={TextSizesEnum.XSmall}>
          <LinesEllipsis text={itemData.body} maxLine={2} />
        </PlexTypography>
      </div>
      <div className={classNames(classes.columnTitle, classes.columnAnalytics)}>
        <PlexTypography text={item.analytics?.audience.toString()} size={TextSizesEnum.NormalPlus} />
      </div>
      <div className={classNames(classes.columnTitle, classes.columnAnalytics)}>
        <PlexTypography text={item.analytics?.impressions.toString()} size={TextSizesEnum.NormalPlus} />
      </div>
      <div className={classNames(classes.columnTitle, classes.columnAnalytics)}>
        <PlexTypography text={item.analytics?.clicks.toString()} size={TextSizesEnum.NormalPlus} />
      </div>
      <div className={classNames(classes.columnTitle, classes.columnAnalytics)}>
        <PlexTypography text={item.analytics?.commentsCount.toString()} size={TextSizesEnum.NormalPlus} />
      </div>
      <div className={classNames(classes.columnTitle, classes.columnAnalytics)}>
        <PlexTypography text={item.analytics?.reactionsCount.toString()} size={TextSizesEnum.NormalPlus} />
      </div>
    </CardActionArea>
  );
});

const useStyles = makeStyles((theme) => ({
  columnType: {
    display: 'flex',
    width: 60,
    marginLeft: 10,
  },
  columnDescription: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: 0,
    borderRadius: 0,
    justifyContent: 'flex-start',
  },
  columnPlex: {
    display: 'flex',
    width: 160,
    marginRight: 16,
  },
  columnVisibility: {
    display: 'flex',
    width: 60,
  },
  columnDate: {
    display: 'flex',
    width: 160,
    flexDirection: 'column',
  },
  eventRoot: {
    display: 'flex',
    flexDirection: 'row',
    padding: 14,
    ...bottomBorderStyle,
  },
  eventTitle: {
    color: colorPalette.primaryColorBlue._500,
  },
  eventText: {
    color: colorPalette.purpleGray._500,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 500,
    marginTop: spacings.small,
  },
  eventPlexName: {
    maxWidth: 160,
    height: 'auto',
  },
  eventPublishStatus: {
    display: 'flex',
    color: colorPalette.purpleGray._500,
    fontSize: theme.typography.fontSize,
  },
  eventDate: {
    color: colorPalette.purpleGray._400,
  },
  columnAnalytics: {
    display: 'flex',
    width: 80,
  },
  columnTitle: {
    display: 'flex',
    color: '#464663',
    fontWeight: 'bold',
  },
}));
